import { useLocation } from "react-router-dom";
import {
	useLazyGetExportMaintenancePdfQuery,
	useLazyGetExportRoutePdfQuery,
	useLazyGetExportStopsPdfQuery,
	useLazyGetExportSummaryPdfQuery,
	useLazyGetExportTripsPdfQuery,
} from "../Redux/service/NewReports/ExportReport";
import {
	useLazyGetAttendanceReportFileQuery,
	useLazyGetDailyAttendanceReportFileQuery,
	useLazyGetDurationFileQuery,
	useLazyGetProductivityFileQuery,
} from "../Redux/service/NewReports/Workforce";
import { useLazyGeServiceRequestFileQuery } from "../Redux/service/NewReports/ServicesRequestReports";
import { useLazyGetShelfMovementReportPdfQuery } from "../Redux/service/NewReports/ShelfMovement";

export const ExportReportPdfHandler = () => {
	const [exportRoutes] = useLazyGetExportRoutePdfQuery();
	const [exportTrips] = useLazyGetExportTripsPdfQuery();
	const [exportStop] = useLazyGetExportStopsPdfQuery();
	const [exportSummary] = useLazyGetExportSummaryPdfQuery();
	const [exportProductivity] = useLazyGetProductivityFileQuery();
	const [exportDurationEfficency] = useLazyGetDurationFileQuery();
	const [exportRequests] = useLazyGeServiceRequestFileQuery();
	const [exportDailyAttendance] = useLazyGetDailyAttendanceReportFileQuery();
	const [exportAttendance] = useLazyGetAttendanceReportFileQuery();
	const [exportMaintenancePdf] = useLazyGetExportMaintenancePdfQuery();
	const [exportShelfMovement] = useLazyGetShelfMovementReportPdfQuery();

	const { pathname } = useLocation();
	const shorturl = pathname.replace(/\/+$/, "").split("/").pop();
	async function getReportBlob({
		shorturl,
		startDate,
		endDate,
		format = "pdf",
	}) {
		if (shorturl === "efficiency") {
			return await exportDurationEfficency({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else if (shorturl === "productivity") {
			return await exportProductivity({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else if (shorturl === "requests") {
			return await exportRequests({ val: format, startDate, endDate }).unwrap();
		} else if (shorturl === "dailyAttendance") {
			return await exportDailyAttendance({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else if (shorturl === "attendance") {
			return await exportAttendance({
				val: format,
				startDate,
				endDate,
			}).unwrap();
		} else {
			throw new Error(`Unknown report type: ${shorturl}`);
		}
	}
	function getReportFilename(shorturl, format = "pdf") {
		if (shorturl === "efficiency") {
			return `durationEfficiency.${format}`;
		} else if (shorturl === "productivity") {
			return `productivity.${format}`;
		} else if (shorturl === "requests") {
			return `requests.${format}`;
		} else if (shorturl === "dailyAttendance") {
			return `Attendance_Report.${format}`;
		} else if (shorturl === "attendance") {
			return `Shift_Compliance.${format}`;
		} else {
			return `report.${format}`; // fallback
		}
	}
	const buildTimestamp = () =>
		new Date()
			.toISOString()
			.replace(/[:.]/g, "-")
			.replace("T", "_")
			.split("Z")[0];
	function formatDate(dateString) {
		return decodeURIComponent(dateString).split("T")[0];
	}
	const handleExportReportPdf = async ({
		fileName,
		exportIds,
		startDate,
		endDate,
		filters: maintenanceFilterQuery = "",
	}) => {
		if (shorturl === "maintenance") {
			if (!fileName) {
				console.warn("Missing export parameters");
				return;
			}
			try {
				const blob = await exportMaintenancePdf({
					filterQuery: maintenanceFilterQuery,
				}).unwrap();
				const url = window.URL.createObjectURL(new Blob([blob]));
				const link = document.createElement("a");
				link.href = url;
				const timestamp = new Date()
					.toISOString()
					.replace(/[:.]/g, "-")
					.replace("T", "_")
					.split("Z")[0];
				link.setAttribute("download", `Maintenance_Report_${timestamp}.pdf`);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			} catch (err) {
				console.error("Export failed", err);
			}
			return;
		}

		if (
			shorturl !== "efficiency" &&
			shorturl !== "productivity" &&
			shorturl !== "requests" &&
			shorturl !== "dailyAttendance" &&
			shorturl !== "attendance" &&
			shorturl !== "shelfMovement"
		) {
			if (!fileName || !exportIds || !startDate || !endDate) {
				console.warn("Missing export parameters");
				return;
			}
		}

		const params = {
			from: startDate,
			to: endDate,
			selectedids: exportIds,
		};

		try {
			if (
				shorturl !== "efficiency" &&
				shorturl !== "productivity" &&
				shorturl !== "requests" &&
				shorturl !== "dailyAttendance" &&
				shorturl !== "attendance"
			) {
				switch (fileName) {
					case "Routes Report": {
						const blob = await exportRoutes(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", "Routes_Report.pdf");
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					case "Trips Report": {
						const blob = await exportTrips(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", "Trips_Report.pdf");
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					case "Stop Report": {
						const blob = await exportStop(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", "Stop_Report.pdf");
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					case "Summary Report": {
						const blob = await exportSummary(params).unwrap();
						const url = window.URL.createObjectURL(new Blob([blob]));
						const timestamp = new Date()
							.toISOString()
							.replace(/[:.]/g, "-")
							.replace("T", "_")
							.split("Z")[0];
						const link = document.createElement("a");
						link.href = url;
						link.setAttribute("download", `Report ${timestamp}.pdf`);
						document.body.appendChild(link);
						link.click();
						link.remove();
						break;
					}
					default:
						console.warn(`No export logic defined for file: ${fileName}`);
						break;
				}
			} else {
				const blob = await getReportBlob({
					shorturl,
					startDate,
					endDate,
					format: "pdf",
				});

				const url = window.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", getReportFilename(shorturl, "pdf"));

				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			}
		} catch (err) {
			console.error("Export failed", err);
		}
		if (shorturl === "shelfMovement") {
			if (!startDate || !endDate) {
				console.warn("Missing export parameters: startDate/endDate required");
				return;
			}
			try {
				const blob = await exportShelfMovement({
					format: "pdf",
					fromDate: formatDate(startDate),
					toDate: formatDate(endDate),
				}).unwrap();
				const url = window.URL.createObjectURL(new Blob([blob]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", `ShelfMovement_${buildTimestamp()}.pdf`);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			} catch (err) {
				console.error("Export failed", err);
			}
			return;
		}
	};

	return { handleExportReportPdf };
};
